import { getProfileAPI, updateProfileAPI } from "@/apis/auth-api"
import Wrapper from "@/components/wrapper"
import { authStore } from "@/routes/login/auth-store"
import { langStore } from "@/stores/lang-store"
import { myPageBundle } from "@/routes/my/my-page-const"
import { Mail, Phone, UserRound, Pencil } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import Flex from "@/components/flex"

const PROFILE_FIELDS = [
  { key: "user_name", label: "name", type: "text", Icon: UserRound },
  { key: "phone", label: "phone", type: "tel", Icon: Phone },
  { key: "email", label: "email", type: "email", Icon: Mail }
]

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 11)

  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`

  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
}

const ProfileView = () => {
  const { lang } = langStore()
  const { user_id, user_name } = authStore()
  const [profile, setProfile] = useState({
    user_id,
    user_name,
    phone: "",
    email: ""
  })
  const [editingField, setEditingField] = useState(null)
  const [draft, setDraft] = useState("")
  const [savingField, setSavingField] = useState(null)
  const bundle = myPageBundle.profile

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await getProfileAPI()
        setProfile(data)
      } catch (error) {
        toast.error(error?.response?.data?.detail ?? bundle.failure[lang])
      }
    }

    loadProfile()
  }, [bundle.failure, lang])

  const startEdit = (key) => {
    const value = String(profile[key] ?? "")

    setEditingField(key)
    setDraft(key === "phone" ? formatPhone(value) : value)
  }

  const cancelEdit = () => {
    setEditingField(null)
    setDraft("")
  }

  const saveField = async (key) => {
    try {
      setSavingField(key)

      const data = await updateProfileAPI({ [key]: draft })
      setProfile(data)

      if (key === "user_name") {
        authStore.getState().setUserName(data.user_name)
      }

      cancelEdit()
      toast.success(bundle.success[lang])
    } catch (error) {
      toast.error(error?.response?.data?.detail ?? bundle.failure[lang])
    } finally {
      setSavingField(null)
    }
  }

  return (
    <Wrapper.shadow className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-0 shadow-sm">
      <div className="p-5 sm:p-6 md:p-7">
        <div className="flex items-center gap-4">
          <div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-toicos-navy">
            <UserRound size={27} strokeWidth={1.7} />
          </div>

          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-bold tracking-tight text-slate-800">
              {profile.user_name || user_name || bundle.title[lang]}
            </h2>
            <p className="mt-1 truncate text-sm text-slate-500">{profile.user_id || user_id}</p>
          </div>

          <span className="hidden rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-500 sm:inline-flex">
            {bundle.title[lang]}
          </span>
        </div>

        <div className="my-6 border-t border-slate-100" />

        <div className="mb-2">
          <h3 className="text-sm font-bold text-slate-800">{bundle.title[lang]}</h3>
          <p className="mt-1 text-xs text-slate-500">{bundle.description[lang]}</p>
        </div>

        <article className="mt-4 divide-y divide-slate-100">
          {PROFILE_FIELDS.map((field) => {
            const { key, label, type, Icon } = field
            const isEditing = editingField === key
            const isSaving = savingField === key

            return (
              <Flex key={key} className="py-4" gap={12}>
                <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-slate-50 text-slate-500">
                  <Icon size={18} strokeWidth={1.8} />
                </div>

                <article className="min-w-0 flex-1">
                  <label
                    htmlFor={`profile-${key}`}
                    className="block text-xs font-medium text-slate-500"
                  >
                    {bundle[label][lang]}
                  </label>

                  {isEditing ? (
                    <div className="mt-1.5 flex flex-1 min-w-0 gap-2">
                      <input
                        id={`profile-${key}`}
                        type={type}
                        value={draft}
                        onChange={(event) =>
                          setDraft(
                            key === "phone" ? formatPhone(event.target.value) : event.target.value
                          )
                        }
                        maxLength={key === "email" ? 255 : key === "user_name" ? 50 : 13}
                        autoFocus
                        disabled={isSaving}
                        className="min-w-0 flex-1 rounded-lg border border-blue-200 bg-white px-3 py-0.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 disabled:bg-slate-50"
                      />

                      <div className="flex shrink-0 items-center gap-1.5">
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={isSaving}
                          className="whitespace-nowrap rounded-lg border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          {bundle.cancel[lang]}
                        </button>

                        <button
                          type="button"
                          disabled={isSaving || !draft.trim()}
                          onClick={() => saveField(key)}
                          className="whitespace-nowrap rounded-lg bg-toicos-navy px-3.5 py-2.5 text-xs font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isSaving ? "..." : bundle.save[lang]}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-1.5 break-all text-sm font-semibold text-slate-800">
                      {profile[key] || (
                        <span className="font-normal text-slate-400">{bundle.empty[lang]}</span>
                      )}
                    </p>
                  )}
                </article>

                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => startEdit(key)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                  >
                    <Pencil size={13} />
                    {bundle.edit[lang]}
                  </button>
                )}
              </Flex>
            )
          })}
        </article>
      </div>
    </Wrapper.shadow>
  )
}

export default ProfileView
